<template>
  <b-modal
    id="claimsModal"
    ref="claimsModal"
    hide-footer
    hide-header
    size="xl"
    @shown="setInitial"
    @hide="unSelectAll()"
  >
    <div>
      <div
        v-if="mode==='create'"
        class="big flex justify-center"
      >
        {{ $t('makeAClaim')|capitalize }}
      </div>
      <div
        v-if="mode==='edit'"
        class="big flex justify-center"
      >
        {{ $t('editClaim')|capitalize }}
      </div>
      <div
        v-if="mode==='edit_bulk'"
        class="big flex justify-center"
      >
        {{ $t('editClaims')|capitalize }}
      </div>
      <div
        v-if="mode==='delete'"
        class="big flex justify-center"
      >
        {{ $t('deleteClaims')|capitalize }}
      </div>
      <div
        v-if="mode==='edit'"
        class="subtitle big flex justify-center"
      >
        {{ rightType ? rightType.name : null }}
      </div>

      <!-- BODY -->
      <div class="p-4 input-container">
        <slot name="details" />

        <div class="medium-bold">
          {{ text }}
        </div>
        <form
          autocomplete="off"
          class="w100p mt-2 flex justify-between"
        >
          <div class="w100p mr-2">
            <div class="text-md py-2">
              {{ $t('from') | capitalize }}:
            </div>
            <DatePicker
              v-model="startDate"
              :placeholder="$t('select_date') | capitalize"
              :class="`w100p ${showError('startDate')}`"
              :disabled-date="afterEndDate"
            />
          </div>
          <div class="w100p ml-2">
            <div class="text-md py-2">
              {{ $t('to') | capitalize }}:
              <span>({{ $t('optional') }})</span>
            </div>
            <DatePicker
              v-model="endDate"
              :placeholder="$t('select_date') | capitalize"
              class="w100p"
              :disabled="!startDate"
              :disabled-date="beforeStartDate"
            />
          </div>
        </form>

        <!-- RIGHT TYPES -->
        <div
          v-if="isBulkMode(mode)"
          class="w100p"
        >
          <div class="text-md py-2">
            {{ $t('rightTypes') | capitalize }}:
          </div>

          <Multiselect
            v-model="rightTypes"
            :placeholder="$t('selectRightTypes')|capitalize"
            :options="rights"
            :class="`w100p ${showError('rightTypes')}`"
            track-by="name"
            :multiple="true"
            label="name"
            :hide-selected="true"
            :show-labels="false"
          />

          <p-check
            :checked="selectAll"
            class="p-icon p-curve p-pulse ineligble-icon"
            color="success"
            @change="selectAll = !selectAll"
          >
            <template v-slot:extra>
              <i class="icon fa fa-check"/>
            </template>
            {{ $t('select_all') }}
          </p-check>
        </div>

        <!-- RIGHT OWNER -->
        <div class="w100p">
          <div class="text-md py-2">
            {{ $t('rightOwner') | capitalize }}:
          </div>

          <Multiselect
            v-model="rightOwner"
            :placeholder="$t('selectRightOwner')|capitalize"
            :options="memberSelectList"
            label="username"
            :disabled="!isMlc"
            :class="`w100p ${showError('rightOwner')}`"
            :hide-selected="true"
            :show-labels="false"
          />
        </div>

        <!-- PAYEE -->
        <div class="w100p">
          <div class="text-md py-2">
            {{ $t('payee') | capitalize }}:
          </div>

          <Multiselect
            v-model="payee"
            :placeholder="$t('selectPayee')|capitalize"
            :options="memberSelectList"
            label="username"
            :class="`w100p ${showError('payee')}`"
            :disabled="!isMlc || isSisterSociety"
            :hide-selected="true"
            :show-labels="false"
          />
        </div>

        <!-- SPLIT -->
        <div v-if="mode!=='delete'" class="w100p">
          <div class="text-md py-2">
            {{ $t('split') | capitalize }} (%):
          </div>
          <div :class="`w100p ${showError('split')}`">
            <input
              @keypress="onlyNumber"
              v-model="split"
              class="w100p"
              type="text"
            >
          </div>
        </div>
      </div>

      <!-- BUTTONS -->
      <div class="m-4 flex justify-end">
        <!-- CANCEL BUTTON -->
        <button
          class="modal-default-button mr-4 w120px px-4 btn border"
          @click="$emit('close', $refs.claimsModal)"
        >
          {{ $t('cancel') |capitalize }}
        </button>
        <!-- CLAIM BUTTON -->
        <span
          class="btn px-4 w120px py-8 big btn-primary"
          @click="makeOperation(mode)"
        >
          <spinner v-if="loading" />
          <span v-else>
            <span v-if="mode==='create'">{{ $t("claim") | capitalize }}</span>
            <span v-else-if="mode==='delete'">{{ $t('delete') | capitalize }}</span>
            <span v-else-if="mode==='edit'">{{ $t('editClaim') | capitalize }}</span>
            <span v-else>{{ $t('save') | capitalize }}</span>
          </span>
        </span>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" scoped>
.w-50px {
  width: 50px;
}

.claims-modal-container {
  padding: 0 450px;
}

div {
  font-family: "CircularStd", sans-serif;
}

.input-container {
  display: flex;
  flex-wrap: wrap;
}

.subtitle {
  color: #6f6f6f;
}

.required {
  border: solid 2px red;
  border-radius: 8px;
  box-sizing: border-box;
}

input {
  height: 48px;
  padding: 6px;
  color: black;
  border: 1px solid #c0c5cf;
  border-radius: 4px;
}
</style>




<script>
import { mapGetters, mapState } from "vuex"
import { ApiCalls } from "@/assets/libs/services"
import { isObject } from "lodash"

export default {
  updated () {
    this.error_field = null
  },
  watch: {
    mode (mode){
      if (this.isBulkMode(mode)) {
        this.initialData = {}
      }
    },
    initial (initial){
      this.initialData = initial
    },
    initialData () {
      this.setInitial()
    },
    selectAll ( selectAll ){
      if (selectAll) {
        this.rightTypes = this.rights
      }
      else {
        this.rightTypes = []
      }
    }
  },
  computed: {
    ...mapState("members", ["societies"]),
    ...mapState("soundsys", ["rights", "countries", "membersList"]),
    ...mapGetters("soundsys", ["isMlc", "userProfile", "isSisterSociety"])
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    text: { type: String },
    soundRecordings: {
      type: Array
    },
    initial: {
      type: Object,
      default: () => {}
    },
    mode: {
      type: String,
      default: "create"
    },
    rightOwners: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      id: null,
      initialData: {},
      selectAll: false,
      loading: false,
      split: 100,
      startDate: null,
      endDate: null,
      rightTypes: null,
      rightType: null,
      rightOwner: null,
      payee:  null,
      memberSelectList: [],
    }
  },
  methods: {
    onlyNumber ($event) {
      let keyCode = ($event.keyCode ? $event.keyCode : $event.which)
      if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
        $event.preventDefault()
      }
    },
    setInitial () {
      // ID, SPLIT, START DATE and END DATE
      const get = this.$options.filters.get
      let initialData = this.initialData
      this.id = get(initialData, "id", null)
      this.split = get(initialData, "split", 100)
      let startDate = get(initialData, "start_date", null)
      this.startDate = startDate ? new Date(startDate) : startDate
      let endDate = get(initialData, "end_date", null)
      this.endDate = endDate ? new Date(endDate) : endDate
      // RIGHT TYPES
      let rightType = get(initialData, "right_type", null)
      this.rightType = rightType ? this.rights.find((right) => right.name == rightType) : null
      this.rightTypes = this.rightType ? [this.rightType] : null


      // PAYEE
      this.payee = null
      if (this.userProfile.member && (!this.isMlc || this.isSisterSociety)) {
        // Payee is the user's member organization, in case it is not an MLC or sister society.
        this.payee = {
          username: this.userProfile.member.username,
          id: this.userProfile.member.id
        }
      } else {
        // If the user's organization is an MLC initial data may be provided.
        let payee = get(initialData, "payee", null)
        this.payee = payee ? this.membersList.find((member) => member.username == payee) : null
      }
      this.memberSelectList = this.membersList
      if(this.mode === "delete"){
        let rightOwnerUsernames = this.rightOwners.map(owner => owner.username)
        let filteredMembersList = this.membersList.filter(member => rightOwnerUsernames.includes(member.username))
        this.memberSelectList = filteredMembersList.length?filteredMembersList: this.membersList
      }

      // RIGHT OWNER
      this.rightOwner = null

      if (this.userProfile.member && !this.isMlc) {
        // Right owner is the user's member organization, in case it is not an MLC.
        this.rightOwner = {
          username: this.userProfile.member.username,
          id: this.userProfile.member.id
        }
      } else {
        // If the user's organization is an MLC initial data may be provided.
        let rightOwner = get(initialData, "right_owner", null)
        this.rightOwner = rightOwner ? this.membersList.find((member) => member.username == rightOwner) : null
      }
    },
    showError (field) {
      if (this.error_field === field) return "required"
      return " "
    },
    beforeStartDate (date) {
      return date < this.startDate
    },
    afterEndDate (date) {
      if(this.endDate) {
        return date > this.endDate
      }
      return false
    },
    validate () {
      const missingFields = [
        "startDate",
        "rightTypes",
        "rightOwner",
        "payee",
        "split"
      ].some((field, index, arr) => {
        const currentField = this[field]
        if (!currentField) {
          this.error_field = arr[index]
          this.$forceUpdate()
        }
        return !currentField
      })
      if (missingFields) {
        const decamelizedField = this.$options.filters.decamelize(
          this.error_field
        )
        this.$toasted.global.toast(`${ this.$t("missing_required_fields") } ${ decamelizedField }`)
        return
      }

      const startDate = this.$options.filters
        .moment(this.startDate)
        .format("YYYY-MM-DD")
      let endDate = null
      if (this.endDate) {
        endDate = this.$options.filters
          .moment(this.endDate)
          .format("YYYY-MM-DD")
      }
      if (endDate && startDate > endDate) {
        this.$toasted.global.toast(this.$t("invalid_data_range"))
        return
      }


      const split = parseFloat(this.split)
      if (isNaN(split)) {
        this.error_field = "split"
        this.$forceUpdate()
        this.$toasted.global.toast(`${ this.$t("invalid_split_value") } ${this.split}`)
        return
      } else if (split < 0 || split > 100) {
        this.error_field = "split"
        this.$forceUpdate()
        this.$toasted.global.toast(this.$t("invalid_split_range"))
        return
      }
      this.split = split

      let data = null
      try {
        data = {
          sound_recordings: this.soundRecordings.map(sr => sr.id),
          start_date: startDate,
          end_date: endDate,
          rights: this.rightTypes.map(right => right.id),
          right_owner_id: this.rightOwner.id,
          payee_id: this.payee.id,
          split: this.split
        }
      } catch (error) {
        this.$toasted.global.toast(this.getToastMessage(error))
      }
      return data

    },
    makeOperation (mode) {
      let data = this.validate()

      if (!data) return

      this.loading = true
      const promise = mode === "create"
        ? this.createClaim(data)
        : this.editClaim(data)

      return promise
        .catch(err => this.$toasted.global.toast(this.getToastMessage(err.response.data)))
        .finally(() => { this.loading = false })
    },
    editClaim (data) {
      if (this.mode === "delete") {
        data.split = 0
      }
      data.claim_id = this.id
      return ApiCalls.claimSelected(data)
        .then(() => {
          this.$emit("claimEdited")
          this.$emit("close", this.$refs.claimsModal, true)
          this.$toasted.global.toast(this.$t("claim_edited"))
        })
    },
    createClaim (data) {
      return ApiCalls.claimSelected(data)
        .then(() => {
          const nOfSrs = this.soundRecordings.length
          this.$toasted.global.toast(`${ nOfSrs } ${ this.$t("sound_recordings_claimed") }`)
          this.$emit("close", this.$refs.claimsModal, true)
        })
    },
    getToastMessage (data) {
      return isObject(data) ? (data.error || data[0]) : data
    },
    isBulkMode (mode) {
      return ["edit_bulk", "delete", "create"].includes(mode)
    },
    unSelectAll () {
      this.selectAll = false
    },
  }
}
</script>
